// src/components/api/zapLink.tsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaWhatsapp } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import * as FaIcons from 'react-icons/fa6';
import Swal from 'sweetalert2';
import './link.css';

const FaWhatsappIcon = FaWhatsapp as React.ComponentType<{ className?: string }>;
const FaWindows = FaIcons.FaWindows as React.ComponentType<{ className?: string }>;
const FaApple = FaIcons.FaApple as React.ComponentType<{ className?: string }>;
const FaLinux = FaIcons.FaLinux as React.ComponentType<{ className?: string }>;
const FaAndroid = FaIcons.FaAndroid as React.ComponentType<{ className?: string }>;
const FaMobile = FaIcons.FaMobile as React.ComponentType<{ className?: string }>;
const FaDownload = FaIcons.FaDownload as React.ComponentType<{ className?: string }>;
const FaUsers = FaIcons.FaUsers as React.ComponentType<{ className?: string }>;
const FaTriangleExclamation = FaIcons.FaTriangleExclamation as React.ComponentType<{ className?: string }>;

interface LocationState {
  fromForm?: boolean;
  name?: string;
}

const WhatsAppLink: React.FC = () => {
  interface DeviceInfo {
    isMobile: boolean;
    os: 'unknown' | 'windows' | 'macos' | 'linux' | 'android' | 'ios';
    hasWhatsApp: boolean;
    isWebBrowser: boolean;
  }

  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    isMobile: false,
    os: 'unknown',
    hasWhatsApp: false,
    isWebBrowser: false
  });
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | undefined;
  const [isCheckingName, setIsCheckingName] = useState<boolean>(true);

  const detectDevice = () => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    let isMobile = false;
    let os: 'unknown' | 'windows' | 'macos' | 'linux' | 'android' | 'ios' = 'unknown';
    let isWebBrowser = false;

    if (window.location.hostname.includes('web.whatsapp.com') ||
      /WebApp/i.test(userAgent)) {
      isWebBrowser = true;
    }

    if (/windows/i.test(userAgent)) {
      os = 'windows';
    } else if (/macintosh|mac os x/i.test(userAgent)) {
      os = 'macos';
    } else if (/linux/i.test(userAgent)) {
      os = 'linux';
    } else if (/android/i.test(userAgent)) {
      isMobile = true;
      os = 'android';
    } else if (/iphone|ipad|ipod/i.test(userAgent)) {
      isMobile = true;
      os = 'ios';
    }

    setDeviceInfo({ isMobile, os, hasWhatsApp: false, isWebBrowser });
  };

  const handleButtonClick = (url: string) => {
    window.location.href = url;
  };

  const renderDeviceMessage = () => {
    if (deviceInfo.isWebBrowser) {
      return (
        <div className="web-warning">
          <FaTriangleExclamation className="warning-icon" />
          <span>Você está acessando pelo navegador web. O acesso ao WhatsApp Web não é permitido. Por favor, use o aplicativo nativo do WhatsApp em seu dispositivo móvel ou instale o WhatsApp Desktop.</span>
        </div>
      );
    }

    if (deviceInfo.isMobile) {
      if (!deviceInfo.hasWhatsApp) {
        return (
          <div className="device-hint">
            {deviceInfo.os === 'ios' ? (
              <>
                <FaApple className="os-icon" />
                <span>Instale o WhatsApp na <strong>App Store</strong> para continuar</span>
              </>
            ) : (
              <>
                <FaAndroid className="os-icon" />
                <span>Instale o WhatsApp na <strong>Google Play Store</strong> para continuar</span>
              </>
            )}
          </div>
        );
      }
      return null;
    } else {
      let icon = null;
      let text = '';

      switch (deviceInfo.os) {
        case 'windows':
          icon = <FaWindows className="os-icon" />;
          text = 'Abra no <strong>Windows</strong> com o WhatsApp';
          break;
        case 'macos':
          icon = <FaApple className="os-icon" />;
          text = 'Abra na <strong>Apple</strong> com o WhatsApp';
          break;
        case 'linux':
          icon = <FaLinux className="os-icon" />;
          text = 'Abra no <strong>Linux</strong> com o WhatsApp';
          break;
        default:
          icon = <FaMobile className="os-icon" />;
          text = 'Abra no <strong>celular</strong> para melhor experiência';
      }

      return (
        <div className="device-hint">
          {icon}
          <span dangerouslySetInnerHTML={{ __html: text }} />
        </div>
      );
    }
  };

  useEffect(() => {
    const state = location.state;
    if (!state?.fromForm || !state?.name) {
      Swal.fire({
        icon: 'warning',
        title: 'Opa!',
        text: 'Acessou sem nome? Pois é! Você precisa preencher seu nome no formulário antes de acessar esta página.',
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/', { replace: true });
        }
      });
      return;
    }
    setIsCheckingName(false);
    detectDevice();
  }, [location.state, navigate]);

  if (isCheckingName || !state?.name) {
    return null;
  }

  if (deviceInfo.isWebBrowser) {
    return (
      <div className="whatsapp-container web-blocked">
        <div className="whatsapp-content">
          <div className="whatsapp-card warning-card">
            <div className="card-icon warning">
              <FaTriangleExclamation className="users-icon" />
            </div>

            <h1 className="card-title">Acesso não permitido</h1>

            <p className="card-description">
              Este conteúdo só está disponível no aplicativo nativo do WhatsApp.
              <br />
              O acesso via WhatsApp Web não é permitido por motivos de segurança.
            </p>

            <div className="action-buttons">
              <button
                onClick={() => handleButtonClick('https://whatsapp.com/download')}
                className="primary-button"
              >
                <FaDownload className="download-icon" />
                Baixar WhatsApp
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="whatsapp-container">
      <Helmet>
        <title>ᴏs ғɪʟᴍᴇs/sᴇ́ʀɪᴇs - Grupo de Thaís</title>
        <meta name="description" content="📱 Entre no grupo exclusivo do WhatsApp." />
      </Helmet>
      <div className="background-animation">
        <div className="gradient-circle circle-1"></div>
        <div className="gradient-circle circle-2"></div>
        <div className="gradient-circle circle-3"></div>
      </div>

      <div className="whatsapp-content">
        <header className="whatsapp-header">
          <div className="logo-container">
            <FaWhatsappIcon className="logo-icon" />
            <span className="logo-text">WhatsApp</span>
          </div>
          <button
            onClick={() => handleButtonClick('https://whatsapp.com/download')}
            className="download-button"
          >
            <FaDownload className="download-icon" />
            <span>Download</span>
          </button>
        </header>

        <main className="whatsapp-main">
          <div className="whatsapp-card">
            <div className="card-icon">
              <FaUsers className="users-icon" />
            </div>

            {state?.name && <p className="user-greeting">Olá, {state.name}!</p>}

            <div className="card-description">
              <p>Você pode entrar no nosso grupo diretamente no WhatsApp ou baixar o aplicativo WhatsApp se ainda não tiver instalado.</p>
              {renderDeviceMessage()}
            </div>

            <div className="action-buttons">
              {deviceInfo.hasWhatsApp || !deviceInfo.isMobile ? (
                <button
                  onClick={() => handleButtonClick("https://acesso.fsdeafs.com.br/OsFilmesSeries")}
                  className="primary-button"
                >
                  {deviceInfo.isMobile ? 'Abrir no WhatsApp' : 'Abrir no WhatsApp'}
                </button>
              ) : null}

              {(!deviceInfo.hasWhatsApp && deviceInfo.isMobile) && (
                <button
                  onClick={() => handleButtonClick('https://whatsapp.com/download')}
                  className="secondary-button"
                >
                  Baixar WhatsApp para {deviceInfo.os === 'ios' ? 'iOS' : 'Android'}
                </button>
              )}
            </div>
          </div>
        </main>

        <footer className="whatsapp-footer">
          <p>{new Date().getFullYear()}&nbsp;&copy;&nbsp;Todos os direitos reservados.</p>
          <p>Este site não é afiliado ao <b>WhatsApp</b>.</p>
        </footer>
      </div>
    </div>
  );
};
export default WhatsAppLink;
